import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { catchError, map, Observable, Subscription } from 'rxjs';
import { ProjectTable } from 'src/app/components/project-table/project-table.component';
import { PsaAuthorizationService } from 'src/app/services/auth/psa-authorization.service';
import {
  EmissionStatus,
  EmissionStatusFilter,
  PsaEmissionConsultFilter,
  PsaEmissionConsultResponse,
  PsaProjectEmission,
} from 'src/app/shared/interfaces/psa-project-emission';
import { EmissionCreditService } from '../../services/emission-credit.service';

@Component({
  standalone: false,
  selector: 'app-emission-consult',
  templateUrl: './emission-consult.component.html',
  styleUrls: ['./emission-consult.component.scss'],
})
export class EmissionConsultComponent extends ProjectTable<PsaProjectEmission> implements OnInit, AfterViewInit, OnDestroy {
  selectedStatus: B3SelectItem[] = [];
  statusSearchMapped: Record<string, string> = {
    Todos: 'Todos',
    'Pendente de verificação do auditor': 'PENDING_AUDITOR_VERIFICATION',
    'Rejeitado pelo auditor': 'REJECTED_BY_AUDITOR',
    'Pendente de confirmação do desenvolvedor': 'PENDING_DEVELOPER_CONFIRMATION',
    'Registrado na B3': 'REGISTERED_IN_B3',
  };

  searchTermProjName = '';
  searchTermSerialCode = '';
  searchStatus: B3SelectItem = this.selectedStatus[0];

  showButtonProjectRegister = false;
  isDetailsDrawerOpen = false;
  selectedEmissionId: number | null = null;

  private loadSubscription?: Subscription;
  private filterEventsEnabled = false;

  constructor(
    private readonly router: Router,
    private readonly emissionConsultService: EmissionCreditService,
    private readonly datePipe: DatePipe,
    private readonly psaAuthorizationService: PsaAuthorizationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadSelectStatus();
    this.initFilters();
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.filterEventsEnabled = true;
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get isVerificationRoute(): boolean {
    return (this.router.url ?? '').includes('confirma-emissao-credito');
  }

  get breadcrumbLeaf(): string {
    return this.isVerificationRoute ? 'Verificação' : 'Consulta';
  }

  get pageTitle(): string {
    return this.isVerificationRoute
      ? 'Verificação de Emissões de Crédito'
      : 'Consulta de Emissões de Crédito';
  }

  get emissionsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    return count === 1
      ? '1 resultado encontrado para o filtro selecionado'
      : `${count} resultados encontrados para o filtro selecionado`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? 10;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  initFilters(): void {
    this.searchTermSerialCode = '';
    this.searchTermProjName = '';
    this.searchStatus = this.selectedStatus[0];
    this.pagination.page = 1;
  }

  loadSelectStatus(): void {
    Object.keys(EmissionStatusFilter).forEach((key, index) => {
      if (isNaN(Number(key))) {
        const value = EmissionStatusFilter[key as keyof typeof EmissionStatusFilter];
        this.selectedStatus.push({ inputId: index + 1, label: value });
      }
    });
  }

  loadDataAndUpdateTable(psaEmissionFilter?: PsaEmissionConsultFilter): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.loadCreditEmissions(psaEmissionFilter).subscribe(emissions => {
      if (!emissions) {
        return;
      }

      const psaFunctions = this.psaAuthorizationService.getFunctions();
      this.showButtonProjectRegister = psaFunctions.some((f: { functionCode?: string }) => f.functionCode === 'CAR_PSA_REGISTRA_PROJETO');

      const allEmissionsDataParsed = this.processData(emissions?.data?.carbonCredits as PsaProjectEmission[]);

      this.itens = allEmissionsDataParsed;
      this.totalItens = emissions?.meta?.totalRecords ?? 0;
      this.totalPages = emissions?.meta?.totalPages
        ?? Math.ceil(this.totalItens / (this.pagination.size ?? 10));
    });
  }

  processData(allEmissions: PsaProjectEmission[]): PsaProjectEmission[] {
    const parsedEmissions = this.emissionConsultService.parseEmissionStatus(allEmissions);

    return parsedEmissions.map(emission => ({
      ...emission,
      insertDate: this.formatInsertDate(emission.insertDate),
      projectName: emission.project?.name,
    }));
  }

  cleanFilters(): void {
    this.initFilters();
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  loadCreditEmissions(psaEmissionFilter?: PsaEmissionConsultFilter): Observable<PsaEmissionConsultResponse | undefined> {
    return this.emissionConsultService.listCreditEmission(psaEmissionFilter).pipe(
      map((res: PsaEmissionConsultResponse) => res || undefined),
      catchError(error => {
        console.error('Erro ao carregar emissões de crédito. Stack error: ', error);
        return [undefined];
      }),
    );
  }

  navigateToProjectRegister(): void {
    this.router.navigate(['/registro-emissao-credito']);
  }

  navigateToPageView(row: PsaProjectEmission): void {
    this.selectedEmissionId = row.id ?? null;
    this.isDetailsDrawerOpen = this.selectedEmissionId != null;
  }

  onDetailsDrawerOpenChange(isOpen: boolean): void {
    this.isDetailsDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedEmissionId = null;
    }
  }

  onEmissionUpdatedInDrawer(): void {
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onFilterTable(): void {
    if (!this.filterEventsEnabled) {
      return;
    }

    this.pagination.page = 1;
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  trackByEmissionId(_index: number, emission: PsaProjectEmission): string | number {
    return emission.id ?? _index;
  }

  formatQuantity(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}`;
  }

  getEmissionDetailLabel(emission: PsaProjectEmission): string {
    return `Visualizar detalhes da emissão ${emission.emissionNumber || emission.serialCode || ''}`.trim();
  }

  getSerialCode(emission: PsaProjectEmission): string {
    const value = emission.serialCode?.trim();
    return value && value !== '-' ? value : '';
  }

  getSerialCodeAriaLabel(emission: PsaProjectEmission): string {
    const value = this.getSerialCode(emission);
    return value
      ? `Serial range ${value}. Clique para copiar.`
      : 'Serial range indisponível';
  }

  copySerialCode(emission: PsaProjectEmission, event: Event): void {
    event.stopPropagation();

    const selectedText = window.getSelection()?.toString().trim();
    if (selectedText) {
      return;
    }

    const value = this.getSerialCode(emission);
    if (!value) {
      return;
    }

    void navigator.clipboard.writeText(value);
  }

  onSerialCodeKeydown(emission: PsaProjectEmission, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.copySerialCode(emission, event);
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case EmissionStatus.PENDING_AUDITOR_VERIFICATION:
        return 'Pendente do Auditor';
      case EmissionStatus.REJECTED_BY_AUDITOR:
      case EmissionStatus.REJECTED_BY_DEVELOPER:
        return 'Rejeitado pelo Auditor';
      case EmissionStatus.PENDING_DEVELOPER_CONFIRMATION:
      case EmissionStatus.ADJUSTED_BY_DEVELOPER:
        return 'Aguardando relatório';
      case EmissionStatus.APPROVED_BY_AUDITOR:
      case EmissionStatus.CONFIRMED_BY_DEVELOPER:
      case EmissionStatus.REGISTERED_IN_B3:
        return 'Registrado na B3';
      default:
        return status ?? '-';
    }
  }

  getStatusTone(status?: string): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    switch (status) {
      case EmissionStatus.REJECTED_BY_AUDITOR:
      case EmissionStatus.REJECTED_BY_DEVELOPER:
        return 'danger';
      case EmissionStatus.APPROVED_BY_AUDITOR:
      case EmissionStatus.CONFIRMED_BY_DEVELOPER:
      case EmissionStatus.REGISTERED_IN_B3:
        return 'success';
      case EmissionStatus.PENDING_AUDITOR_VERIFICATION:
        return 'warning';
      case EmissionStatus.PENDING_DEVELOPER_CONFIRMATION:
      case EmissionStatus.ADJUSTED_BY_DEVELOPER:
        return 'info';
      default:
        return 'info';
    }
  }

  getStatusIcon(status?: string): string {
    switch (status) {
      case EmissionStatus.REJECTED_BY_AUDITOR:
      case EmissionStatus.REJECTED_BY_DEVELOPER:
        return 'block';
      case EmissionStatus.APPROVED_BY_AUDITOR:
      case EmissionStatus.CONFIRMED_BY_DEVELOPER:
      case EmissionStatus.REGISTERED_IN_B3:
        return 'check_circle';
      case EmissionStatus.PENDING_AUDITOR_VERIFICATION:
      case EmissionStatus.PENDING_DEVELOPER_CONFIRMATION:
      case EmissionStatus.ADJUSTED_BY_DEVELOPER:
        return 'sync';
      default:
        return 'sync';
    }
  }

  private formatInsertDate(value?: string | null): string {
    if (!value?.trim()) {
      return '-';
    }

    const trimmed = value.trim();
    const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day}/${month}/${year}`;
    }

    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime())) {
      return trimmed.split(' ')[0] || trimmed;
    }

    return this.datePipe.transform(parsed, 'dd/MM/yyyy') ?? trimmed;
  }

  private buildCurrentFilter(): PsaEmissionConsultFilter {
    return {
      projectName: this.searchTermProjName || '',
      serialCode: this.searchTermSerialCode || '',
      status: this.searchStatus.label !== 'Todos'
        ? this.statusSearchMapped[this.searchStatus.label || '']
        : '',
      page: this.pagination.page || 1,
      pageSize: this.pagination.size || 10,
    };
  }
}
